import { useCallback, useEffect, useState, useMemo } from "react";
import ReactTableWrapper from "../../components/ReactTableWrapper";
import { exportAsCSV } from "../../utils/CSVExporter";
import { exportAsPDF } from "../../utils/PDFExporter";
import { useNavigate } from "react-router-dom";
import "../../App.css";
import ListFormPageHeader from "../../components/ListFormPageHeader";
import Sidebar from '../../Sidebar';
import Swal from "sweetalert2";
import { useCookies } from 'react-cookie';
import { getAllEquipments, deleteEquipments, getEquipmentUtilizationDetails } from "../../services/EquipmentServices";
import { getHTMLForTablePopup } from "../../utils/PopupUtils";
import { displayError } from "../../helpers";

const clickableCellStyle = () => ({
    color: '#2563eb',
    cursor: 'pointer',
    textDecoration: 'underline',
    fontWeight: 600,
});

function formatNumber(value) {
    const number = Number(value);
    if (!Number.isFinite(number)) {
        return '0';
    }
    return Number.isInteger(number) ? String(number) : number.toFixed(3).replace(/\.?0+$/, '');
}

function showUtilizationPopup({ title, subtitle, columns, rows, footerRow, emptyMessage }) {
    if (!rows.length) {
        Swal.fire(emptyMessage, '', 'info');
        return;
    }
    Swal.fire({
        title,
        html: getHTMLForTablePopup(subtitle, columns, rows, footerRow, emptyMessage),
        width: '1200px',
        confirmButtonText: 'Close',
    });
}

function EquipmentTable() {
    const InitialData = {
        total_pages: 0,
        records: [],
      };
    const [data, setData] = useState(InitialData);
    const navigate = useNavigate(); 
    const [refreshKey, setRefreshKey] = useState(0);
    const [cookies] = useCookies(['myToken']);
    const [isLoading, setIsLoading] = useState(true);

    const openUtilizationDetails = useCallback((row, metric) => {
        if (!row?.id) {
            return;
        }
        Swal.fire({
            title: 'Loading...',
            allowOutsideClick: false,
            didOpen: () => Swal.showLoading(),
        });
        getEquipmentUtilizationDetails(cookies, row.id)
            .then((response) => {
                const trips = response?.trips || [];
                const equipmentName = response?.equipment?.equip_name || row.equip_name || '';
                const totals = response?.totals || {};
                const titleByMetric = {
                    km: 'Total KM Details',
                    hm: 'Total HM Details',
                    qty: 'Total Qty Details',
                };
                const subtitleByMetric = {
                    km: `${equipmentName} — Trip details`,
                    hm: `${equipmentName} — Trip details`,
                    qty: `${equipmentName} — Trip details`,
                };

                const detailRows = trips.map((trip) => ({
                    plant: trip.plant || '',
                    dc_no: trip.dc_no || '',
                    date: trip.date || '',
                    time: trip.time || '',
                    customer: trip.customer || '',
                    site: trip.site || '',
                    grade: trip.grade || '',
                    dc_qty: formatNumber(trip.dc_qty),
                    delivery_mode: trip.delivery_mode || '',
                    total_km: formatNumber(trip.total_km),
                    total_hm: formatNumber(trip.total_hm),
                    driver_name: trip.driver_name || '',
                    invoice_status: trip.invoice_status || '',
                }));

                showUtilizationPopup({
                    title: titleByMetric[metric] || 'Trip Details',
                    subtitle: subtitleByMetric[metric] || equipmentName,
                    columns: [
                        { key: 'plant', label: 'PLANT' },
                        { key: 'dc_no', label: 'DC NO' },
                        { key: 'date', label: 'DATE' },
                        { key: 'time', label: 'TIME' },
                        { key: 'customer', label: 'CUSTOMER' },
                        { key: 'site', label: 'SITE' },
                        { key: 'grade', label: 'GRADE' },
                        { key: 'dc_qty', label: 'DC QTY', align: 'right' },
                        { key: 'delivery_mode', label: 'MODE OF DELIVERY' },
                        { key: 'total_km', label: 'KM', align: 'right' },
                        { key: 'total_hm', label: 'HM', align: 'right' },
                        { key: 'driver_name', label: 'DRIVER NAME' },
                        { key: 'invoice_status', label: 'INVOICE STATUS' },
                    ],
                    rows: detailRows,
                    footerRow: {
                        plant: 'TOTAL',
                        dc_no: '',
                        date: '',
                        time: '',
                        customer: '',
                        site: '',
                        grade: '',
                        dc_qty: formatNumber(totals.total_qty ?? row.total_qty),
                        delivery_mode: '',
                        total_km: formatNumber(totals.total_km ?? row.total_km),
                        total_hm: formatNumber(totals.total_hm ?? row.total_hm),
                        driver_name: '',
                        invoice_status: '',
                    },
                    emptyMessage: 'No trip details found for this equipment.',
                });
            })
            .catch((error) => {
                Swal.close();
                displayError(error.response?.data ?? error.message, 'Failed to load utilization details');
            });
    }, [cookies]);

    const columns = useMemo(
        () => [
            {
                fieldName: 'equip_type', 
                headerName: 'Vehicle Type'
            },
           
            {
                fieldName: "equip_name",
                headerName: 'Vehicle No./SI.No.'
            },
            {
                fieldName: "capacity",
                headerName: 'Capacity'
            },
            {
                fieldName: "vendor",
                headerName: 'Vendor'
            },
            {
                fieldName: "total_km",
                headerName: 'Total KM',
                align: 'right',
                getCellStyle: clickableCellStyle,
                onCellClick: (row) => openUtilizationDetails(row, 'km'),
            },
            {
                fieldName: "total_hm",
                headerName: 'Total HM',
                align: 'right',
                getCellStyle: clickableCellStyle,
                onCellClick: (row) => openUtilizationDetails(row, 'hm'),
            },
            {
                fieldName: "total_qty",
                headerName: 'Total Qty',
                align: 'right',
                getCellStyle: clickableCellStyle,
                onCellClick: (row) => openUtilizationDetails(row, 'qty'),
            },
            {
                fieldName: "insurance_date",
                headerName: 'Insurance Due Date'
            },
            {
                fieldName: "permit_date",
                headerName: 'Permit Due Date'
            },
            {
                fieldName: "fc_date",
                headerName: 'FC Due Date'
            },
            {
                fieldName: "meter_status",
                headerName: 'Meter Status'
            },
            {
                fieldName: "user_remarks",
                headerName: 'Remarks'
            },
            {
                fieldName: "status",
                headerName: 'Status'
            },
            {
                fieldName: "id",
                headerName: "ROW_ACTIONS"
            }
       
         
        ],
        [openUtilizationDetails]);

    const onEditEquipment = (equipmentId, event) => {
       
       navigate(`/EquipmentEdit/${equipmentId}/`);
    };

    const onDeleteEquipment = (equipmentId, event) => {

        event.preventDefault();
        Swal.fire({title: 'Are you sure to Delete?',  
        showCancelButton: true,  
        confirmButtonText: `Yes`,  
        cancelButtonText: `No`,
        }).then((result) => {  
          if (result.isConfirmed) { 
            setIsLoading(true);
            deleteEquipments(cookies, equipmentId); 
            console.log(`Eqipment with id ${equipmentId} deleted`);
            Swal.fire('Deleted Successfully!', '', 'success');
            setRefreshKey(oldKey => oldKey +1)
          } else if (result.isDismissed) {    
            Swal.fire('Not Deleted', '', 'info')  
        }
        });
        //

    };

    const handleCSVExport = (event) => {
        console.log('CSV Export');
        exportAsCSV(
            columns.filter(col => col.headerName !== 'ROW_ACTIONS'),
            data.records
        );
    };

    const handlePDFExport = (event) => {
        console.log('PDF Export');
        exportAsPDF(
            'List of Equipment Form',
            columns
            .filter(col => col.headerName !== 'ROW_ACTIONS')
            .map(col=>({header: col.headerName, dataKey: col.fieldName })),
            data.records.map(r => ({
                equip_type: r.equip_type ?? '',
                equip_name: r.equip_name ?? '',
                capacity:r.capacity ?? '',
                vendor:r.vendor ?? '',
                total_km: r.total_km ?? 0,
                total_hm: r.total_hm ?? 0,
                total_qty: r.total_qty ?? 0,
                insurance_date:r.insurance_date ?? '',
                permit_date:r.permit_date ?? '',
                fc_date:r.fc_date ?? '',
                meter_status:r.meter_status ?? '',
                user_remarks:r.user_remarks ?? '',
                status:r.status ?? '',
            })),
            'Equipment_Forms.pdf'
        );
    };

    useEffect(() => {
        if (!cookies.myToken) {
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        getAllEquipments(cookies)
        .then( equipmentsList => {
            const tableData = (equipmentsList?.equipment_list || [])
            .map(r => ({
                id :r.id,
                equip_type: r.equip_type?.name ?? '',
                equip_name: r.equip_name ?? '',
                capacity:r.capacity ?? '',
                vendor:r.vendor?.name ?? '',
                total_km: r.total_km ?? 0,
                total_hm: r.total_hm ?? 0,
                total_qty: r.total_qty ?? 0,
                insurance_date:r.insurance_date ?? '',
                permit_date:r.permit_date ?? '',
                fc_date:r.fc_date ?? '',
                meter_status:r.meter_status ? "working":"not_working",
                user_remarks:r.user_remarks ?? '',
                status:r.status ? "active":"inactive" ,
                hide_row_edit:r.system_field?"Yes":"No",
                hide_row_delete:r.system_field?"Yes":"No"
            }));

            setData({
                total_pages: 0,
                records: tableData
            });
        })
        .catch((error) => {
            setData(InitialData);
            displayError(error.response?.data ?? error.message, 'Failed to load equipment list');
        })
        .finally(() => {
            setIsLoading(false);
        });
    }, [cookies, cookies.myToken, refreshKey]);
    
    return (

        <div id="outer-container"  className="App" > 
            <Sidebar pageWrapId={'page-wrap'} outerContainerId={'outer-container'} />
            <div id="page-wrap">
            <ListFormPageHeader module="TRANSPORT" title="List of Equipment" formLink="/Equipment" addNewLabel="New Equipment" />
                <div className="container item-list-table-container">
                <div className="table-responsive">
                <ReactTableWrapper
                            title='List of Group Form'
                            columns={columns}
                            data={data.records}
                            onRowEdit={onEditEquipment}
                            onRowDelete={onDeleteEquipment}
                            onCSVExport={handleCSVExport}
                            onPDFExport={handlePDFExport}
                            isLoading={isLoading}
                        
                        />
                </div>
                </div> 


            </div>
            <button style={{position:"relative",right:"40px"}}className="btn btn-fill btn-primary float-end" type="button" onClick={() =>{navigate("/Equipment")}}>Back</button>
        </div>

        
    );

}



export default EquipmentTable;
